import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { TopbarComponent } from './topbar.component';
import { SidebarComponent } from './app-sidebar.component';
import { BottomNavComponent } from './bottom-nav.component';
import { NetworkService } from '../services/network.service';
import { LayoutService } from '../services/layout.service';
import { BreadcrumbBarComponent, BreadcrumbItem } from '../shared/components/breadcrumb-bar.component';

@Component({
    selector: 'app-layout',
    imports: [CommonModule, RouterModule, TopbarComponent, SidebarComponent, BottomNavComponent, BreadcrumbBarComponent],
    template: `
    <div class="layout-container">
      <!-- MOBILE_UX_SPEC Story 12 — offline banner. -->
      <div *ngIf="!networkService.isOnline()" class="offline-banner">
        <i class="pi pi-wifi"></i>
        No connection — showing cached data
      </div>

      <!-- Topbar — hamburger calls LayoutService directly; no @Output passthrough. -->
      <app-topbar></app-topbar>

      <!-- Sidebar — reads its open state from LayoutService; no @Input/@Output. -->
      <app-sidebar></app-sidebar>

      <!-- Main Content — sidebar-hidden class follows the service's sidebarOpen$. -->
      <main class="layout-main-container"
            [ngClass]="{ 'sidebar-hidden': !(layout.sidebarOpen$ | async) }">
        <!-- Breadcrumbs (#8) — inside the scroll container so sticky top:0 works correctly
             below the fixed topbar, without adding phantom height to the flex layout. -->
        <app-breadcrumb-bar [items]="breadcrumbs"></app-breadcrumb-bar>
        <div class="layout-main">
          <router-outlet></router-outlet>
        </div>
      </main>

      <!-- MOBILE_UX_SPEC Story 1 — Bottom navigation (iPhone only).
           SCRUM-813 — deferred so its module stays out of the initial bundle
           on desktop loads. Triggered on idle so iPhone users still see it
           almost immediately after first paint. -->
      @defer (on idle) {
        <app-bottom-nav></app-bottom-nav>
      }

      <!-- ▼ TEMP iPhone debug overlay — remove once menu bug is fixed.
           Toggle with ?debug=1 in the URL. Shows the last 12 layout/menu
           events so the symptom can be screenshotted from the iPhone. -->
      <div *ngIf="debugVisible" class="debug-overlay">
        <div class="debug-head">menu debug ({{ debugLines.length }})
          <button (click)="debugLines = []; layout._dbgLines = []">clr</button>
        </div>
        <pre>{{ debugLinesText }}</pre>
      </div>
    </div>
  `,
    styles: [`
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }

    .layout-container {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100vh; /* fallback */
      height: 100dvh; /* iOS 15.4+: adjusts when address bar shows/hides */
      overflow: hidden;
    }

    .layout-main-container {
      flex: 1;
      /* match topbar height which expands by safe-area-inset-top on notched iPhones */
      margin-top: 70px;
      margin-top: calc(70px + env(safe-area-inset-top, 0px));
      margin-left: 250px;
      overflow-y: auto;
      overflow-x: hidden;
      transition: margin-left 0.3s ease;
    }

    .layout-main-container.sidebar-hidden {
      margin-left: 0;
    }

    .layout-main {
      padding: 2rem;
      min-height: calc(100dvh - 70px - env(safe-area-inset-top, 0px));
    }

    /* Tablet/iPad (MOBILE_UX_SPEC Story 5): 64px icon-rail sidebar. */
    @media screen and (min-width: 769px) and (max-width: 1199px) {
      .layout-main-container {
        margin-left: 64px;
      }

      .layout-main-container.sidebar-hidden {
        margin-left: 0;
      }

      .layout-main {
        padding: 1.25rem;
      }

      /* SCRUM-2285 — Tesla (~1088px) uses the full 250px labeled sidebar, so the
         content must clear it, not the 64px rail. Non-Tesla tablets unchanged. */
      :host-context(html.tesla) .layout-main-container {
        margin-left: 250px;
      }
      :host-context(html.tesla) .layout-main-container.sidebar-hidden {
        margin-left: 0;
      }
    }

    /* Mobile: sidebar overlays, no margin shift */
    @media screen and (max-width: 768px) {
      .layout-main-container {
        margin-left: 0 !important;
        /* Topbar wraps to 112px on mobile + safe-area-inset-top on notched iPhones. */
        margin-top: 112px;
        margin-top: calc(112px + env(safe-area-inset-top, 0px));
        /* MOBILE_UX_SPEC Story 1 — leave room for the 56px bottom nav + safe area. */
        padding-bottom: calc(72px + env(safe-area-inset-bottom, 0px));
      }

      .layout-main {
        padding: 1rem;
        min-height: calc(100dvh - 112px - env(safe-area-inset-top, 0px));
      }
    }

    @media screen and (max-width: 576px) {
      .layout-main-container {
        margin-top: 108px;
        margin-top: calc(108px + env(safe-area-inset-top, 0px));
      }

      .layout-main {
        padding: 0.75rem;
        min-height: calc(100dvh - 108px - env(safe-area-inset-top, 0px));
      }
    }

    /* Smooth scrolling */
    .layout-main-container {
      scroll-behavior: smooth;
    }

    /* Hide scrollbar but keep functionality (webkit) */
    .layout-main-container::-webkit-scrollbar {
      width: 6px;
    }

    .layout-main-container::-webkit-scrollbar-track {
      background: transparent;
    }

    .layout-main-container::-webkit-scrollbar-thumb {
      background: var(--surface-border);
      border-radius: 3px;
    }

    .layout-main-container::-webkit-scrollbar-thumb:hover {
      background: var(--text-color-secondary);
    }

    /* TEMP debug overlay */
    .debug-overlay {
      position: fixed;
      top: 120px;
      right: 4px;
      width: 250px;
      max-height: 60vh;
      overflow-y: auto;
      background: rgba(0, 0, 0, 0.85);
      color: #0f0;
      font-family: monospace;
      font-size: 10px;
      padding: 4px 6px;
      border-radius: 4px;
      z-index: 99999;
      pointer-events: auto;
    }
    .debug-head {
      display: flex;
      justify-content: space-between;
      color: #ff0;
      margin-bottom: 2px;
    }
    .debug-head button {
      background: #333;
      color: #fff;
      border: 1px solid #666;
      padding: 0 6px;
      font-size: 10px;
    }
    .debug-overlay pre {
      margin: 0;
      white-space: pre-wrap;
      word-break: break-all;
    }
  `]
})
export class AppLayoutComponent implements OnInit {
  networkService = inject(NetworkService);
  layout = inject(LayoutService);
  breadcrumbs: BreadcrumbItem[] = [];

  /** ?debug=1 → shows the on-screen menu-state overlay (iPhone debugging). */
  debugVisible = false;
  debugLines: string[] = [];
  get debugLinesText(): string { return this.debugLines.slice(-12).join('\n'); }

  private readonly router = inject(Router);

  buildBreadcrumbs(url: string): BreadcrumbItem[] {
    const admin = { label: 'Admin', route: '/admin' };
    // Deployments / Logs / QA / Epics / Pipeline removed 2026-07-01 —
    // now served from hub.alphagroves.com. Bookmarked URLs redirect to
    // /admin/users (see admin.routes.ts), so no breadcrumbs are needed.
    if (url === '/admin/bridge') return [admin, { label: 'VS Code Bridge' }];
    if (url === '/admin/users') return [admin, { label: 'Users' }];
    if (url === '/admin/status') return [admin, { label: 'System Status' }];
    if (url === '/admin/health') return [admin, { label: 'Environment Health' }];
    if (url === '/admin/dividend-universe') return [admin, { label: 'Dividend Universe' }];
    if (url === '/admin/stocks') return [admin, { label: 'All Stocks' }];
    // SCRUM-1565 — Providers Console (API Keys + AI Models + AI cost dashboard).
    // startsWith so ?tab= params resolve to the single breadcrumb.
    if (url === '/admin/providers' || url.startsWith('/admin/providers?')) return [admin, { label: 'Providers' }];
    if (url === '/admin/ai/models') return [admin, { label: 'AI Models' }];
    if (url === '/admin/ai/models/audit') return [admin, { label: 'AI Models', route: '/admin/ai/models' }, { label: 'Audit' }];
    // SCRUM-1463 — merged AI dashboard. Match with startsWith so ?tab= params
    // and any child paths still resolve to a single breadcrumb.
    if (url === '/admin/ai' || url.startsWith('/admin/ai?')) return [admin, { label: 'AI' }];
    // Legacy paths — router redirects them, but breadcrumb should still label
    // sensibly if a partial navigation event fires before the redirect lands.
    if (url === '/admin/ai/usage') return [admin, { label: 'AI', route: '/admin/ai' }, { label: 'Summary' }];
    if (url === '/admin/ai/requests') return [admin, { label: 'AI', route: '/admin/ai' }, { label: 'Requests' }];
    if (url === '/admin/ai/pricing') return [admin, { label: 'AI', route: '/admin/ai' }, { label: 'Pricing' }];
    return [];
  }

  ngOnInit(): void {
    this.breadcrumbs = this.buildBreadcrumbs(this.router.url);
    this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe((e) => {
      this.breadcrumbs = this.buildBreadcrumbs((e as NavigationEnd).urlAfterRedirects); // NOSONAR S4325 — intentional type assertion preserved for clarity
    });

    // TEMP — debug overlay toggle (iPhone diagnostics).
    const params = new URLSearchParams(window.location.search); // NOSONAR S7764 — browser-only Angular code
    if (params.get('debug') === '1' || localStorage.getItem('hub_debug') === '1') {
      this.debugVisible = true;
      localStorage.setItem('hub_debug', '1');
      this.layout._dbgEnabled = true;
      this.layout._dbgLines = this.debugLines;

      // Capture every pointerdown/click reaching the hamburger zone, so we
      // can tell whether iOS Safari is even delivering events when the
      // menu "stops opening".
      const describe = (el: EventTarget | null): string => {
        if (!(el instanceof Element)) return '?';
        const tag = el.tagName.toLowerCase();
        const cls = (el.className || '').toString().split(' ').filter(Boolean).slice(0, 2).join('.');
        const aria = el.getAttribute('aria-label') ?? '';
        return `${tag}${cls ? '.' + cls : ''}${aria ? `[${aria.slice(0, 12)}]` : ''}`; // NOSONAR S4624 — nested template kept inline for readability
      };
      window.addEventListener('pointerdown', (e: PointerEvent) => { // NOSONAR S7764 — browser-only Angular code
        this.layout.dbg(`PD x=${Math.round(e.clientX)} y=${Math.round(e.clientY)} t=${describe(e.target)}`);
      }, { passive: true, capture: true });
      window.addEventListener('click', (e: MouseEvent) => { // NOSONAR S7764 — browser-only Angular code
        this.layout.dbg(`CK x=${Math.round(e.clientX)} y=${Math.round(e.clientY)} t=${describe(e.target)}`);
      }, { capture: true });
    }
  }
}
